import { MaskedLines, Reveal, Chapter } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Differentiator } from "@/components/Differentiator";
import { CTASection } from "@/components/CTASection";

const SERVICES = [
  {
    n: "01",
    title: "Executive Coaching",
    who: "CEOs, Presidents, VPs, new executives, military leaders transitioning to civilian leadership, women in executive roles, and high-potential leaders.",
    offers: [
      "Executive coaching (6- or 12-month engagements)",
      "Leadership assessments",
      "Career transition coaching",
      "Executive onboarding",
      "Leadership retreats",
      "Team coaching",
    ],
    deliverables: null,
  },
  {
    n: "02",
    title: "Fractional COO",
    who: "Companies with 20–250 employees — technology firms, professional services, engineering firms, defence companies, and growing startups.",
    offers: [
      "Scaling operations",
      "Strategic planning",
      "KPI dashboards",
      "Executive leadership",
      "Process improvement",
      "Organizational redesign",
      "Board reporting",
      "Budget planning",
      "AI implementation strategy",
    ],
    deliverables: null,
  },
  {
    n: "03",
    title: "PMO Development & Transformation",
    who: null,
    offers: [
      "PMO assessments",
      "PMO design",
      "Governance frameworks",
      "Portfolio management",
      "Project recovery",
      "Executive reporting",
      "Project prioritization",
      "Change management",
      "AI-enabled PMO",
    ],
    deliverables: [
      "PMO Playbook",
      "Governance Framework",
      "Executive Dashboard",
      "Portfolio Review Process",
      "PMO Training",
    ],
  },
  {
    n: "04",
    title: "AI Transformation Advisory",
    who: "Organizations adopting AI across workflows, systems, and teams — with as much focus on the people side as the technology.",
    offers: [
      "AI readiness and opportunity assessment",
      "AI workflow and systems strategy",
      "Implementation roadmaps",
      "Change management for AI adoption",
      "Workforce and staffing strategy for AI-enabled teams",
      "Leadership alignment on AI strategy",
    ],
    deliverables: null,
  },
];

const ADDITIONAL = [
  ["Leadership Workshops", "Custom sessions to build leadership capability across your team."],
  ["Strategic Planning Facilitation", "Structured facilitation to align leadership on direction and priorities."],
  ["Board Advisory", "Strategic counsel for boards navigating growth, transition, or transformation."],
  ["Keynote Speaking", "Speaking engagements on leadership, transformation, and AI adoption."],
  ["Online Leadership Coaching", "Flexible, virtual coaching for leaders anywhere."],
];

export default function Services() {
  return (
    <main data-testid="services-page">
      {/* HEADER */}
      <section className="relative overflow-hidden pt-40 pb-20 md:pt-48 md:pb-28">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 15% 0%, rgba(43,217,199,0.08), transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <p className="overline-label mb-8">Services</p>
          </Reveal>
          <h1
            className="max-w-5xl font-serif text-4xl leading-[1.02] tracking-tighter text-white sm:text-5xl md:text-7xl"
            data-testid="services-headline"
          >
            <MaskedLines
              lines={[
                "One Partner.",
                <span key="e" className="italic text-teal">Every Dimension of Transformation.</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
              From the boardroom to the org chart to the AI roadmap, QuInflection works across
              the full picture of change — so nothing falls through the cracks between strategy
              and execution.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CORE SERVICES */}
      {SERVICES.map((s, idx) => (
        <section
          key={s.n}
          className={`border-t border-white/10 ${idx % 2 === 1 ? "bg-navy-deep" : ""}`}
          data-testid={`service-${s.n}`}
        >
          <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
            <div className="grid gap-12 md:grid-cols-12">
              <div className="md:col-span-5">
                <Chapter index={s.n} label="Service" className="md:sticky md:top-28" />
                <Reveal delay={0.1}>
                  <h2 className="mt-8 font-serif text-3xl leading-tight tracking-tight text-white sm:text-4xl">
                    {s.title}
                  </h2>
                </Reveal>
                {s.who && (
                  <Reveal delay={0.2}>
                    <p className="mt-6 font-sans text-sm leading-relaxed text-white/50">
                      <span className="overline-label mb-2 block">Who it's for</span>
                      {s.who}
                    </p>
                  </Reveal>
                )}
              </div>
              <div className="md:col-span-7">
                <p className="overline-label mb-6">What we offer</p>
                <ul className="grid gap-x-10 sm:grid-cols-2">
                  {s.offers.map((o, i) => (
                    <Reveal key={o} delay={i * 0.04} y={14}>
                      <li className="flex items-start gap-3 border-b border-white/10 py-4">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                        <span className="font-sans text-sm leading-relaxed text-white/75">{o}</span>
                      </li>
                    </Reveal>
                  ))}
                </ul>
                {s.deliverables && (
                  <Reveal delay={0.15}>
                    <div className="mt-10">
                      <p className="overline-label mb-5">What you get</p>
                      <div className="flex flex-wrap gap-3">
                        {s.deliverables.map((d) => (
                          <span
                            key={d}
                            className="rounded-full border border-teal/40 px-4 py-2 font-sans text-xs tracking-wide text-teal"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ADDITIONAL SERVICES */}
      <section className="border-t border-white/10" data-testid="additional-services">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="05" label="Also Available" />
          <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
            {ADDITIONAL.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.05} y={16}>
                <div className="group grid gap-2 py-6 md:grid-cols-12 md:gap-8" data-testid={`additional-${i}`}>
                  <h3 className="font-serif text-xl text-white transition-transform duration-300 group-hover:translate-x-2 md:col-span-5">
                    {title}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed text-white/50 md:col-span-7">
                    {desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <div className="border-t border-white/10 bg-navy-deep">
        <Differentiator />
      </div>

      <CTASection
        testid="services-cta"
        headline="Not Sure Where to Start?"
        body="Most engagements start with a conversation, not a proposal. Let's talk about what's actually going on in your business."
      />
    </main>
  );
}
